import { useEffect, useState } from 'react';
import { endpoints } from '../../api';
import type { SelfProfile, UserAvatar } from '../../api/people';
import { useAccountLocale } from '../../i18n';
import { artworkUrl } from '../studio/studio-art-model';
import { useStudioArtwork } from '../studio/use-studio-artwork';
import type { Performer } from './reels-cast';

const ages: Record<string, Performer['age']> = { kid: 'kid', adult: 'adult', old: 'old' };
const ageWord: Record<string, string> = { kid: 'ребёнок', adult: 'взрослый', old: 'пожилой' };
const sex = (value: string | null | undefined): Performer['gender'] =>
  value === 'm' || value === 'male' ? 'm' : value === 'f' || value === 'female' ? 'f' : null;
const photos = (n: number) => (n ? `${n} фото` : 'нет фото');

/** «Вы» (свои фото или выбранный вариант), близкие и персонажи каталога — те же данные, что у других рядов
    «Кто в кадре» (`GET /app/people/me`, `GET /app/people`, `GET /app/catalog/avatars`). Готовность исполнителя
    подтверждает сервер заказа повторно. */
export function useReelsPerformers(avatar: UserAvatar | null, enabled: boolean) {
  const [own, setOwn] = useState<{ list: Performer[]; self: SelfProfile | null; error: boolean; loaded: boolean }>({ list: [], self: null, error: false, loaded: false });
  const [attempt, setAttempt] = useState(0);
  const locale = useAccountLocale() === 'ru' ? 'ru' : 'en';
  const artwork = useStudioArtwork(locale, enabled ? attempt : -1);
  useEffect(() => {
    if (!enabled) return;
    let active = true;
    void Promise.all([endpoints.people.self(), endpoints.people.list()])
      .then(([self, people]) => {
        if (!active) return;
        const variant = avatar?.status === 'ready';
        const me: Performer = variant
          ? { ref: `user_avatar:${avatar!.id}`, name: 'Вы', image: avatar!.image_url, type: 'avatar', age: 'adult', pet: false, gender: null, ready: true, sub: `вариант «${avatar!.variant_name}»` }
          : {
              ref: 'self',
              name: 'Вы',
              image: self.photos?.[0]?.url,
              type: 'me',
              age: 'adult',
              pet: false,
              gender: null,
              ready: self.ready,
              sub: photos(self.photos?.length ?? 0),
              notReady: 'Добавьте свои фото или вариант',
            };
        setOwn({
          list: [
            me,
            ...people.items.map(person => ({
              ref: `person:${person.id}`,
              name: person.name,
              image: person.photos?.[0]?.url,
              type: 'person' as const,
              age: person.is_pet ? null : (ages[person.age ?? ''] ?? 'adult'),
              pet: person.is_pet,
              gender: sex(person.gender),
              ready: person.selectable && (person.photos?.length ?? 0) > 0,
              sub: [person.rel, person.is_pet ? '' : ageWord[person.age ?? ''], photos(person.photos?.length ?? 0)].filter(Boolean).join(' · '),
              notReady: person.disabled_reason || `Добавьте фото: ${person.name}`,
              person,
            })),
          ],
          self,
          error: false,
          loaded: true,
        });
      })
      .catch(() => {
        if (active) setOwn({ list: [], self: null, error: true, loaded: true });
      });
    return () => {
      active = false;
    };
  }, [avatar, enabled, attempt]);
  const chars: Performer[] = (artwork.data?.avatars ?? []).map(card => ({
    ref: `character:${card.id}`,
    name: card.name || card.full_name,
    image: artworkUrl(card.image_url),
    type: 'char',
    age: 'adult',
    pet: false,
    gender: sex(card.sex),
    ready: true,
    collections: card.collection_ids,
  }));
  return {
    performers: [...own.list, ...chars],
    self: own.self,
    loaded: own.loaded && (!!artwork.data || artwork.error),
    error: own.error,
    charsError: artwork.error && !artwork.data,
    retry: () => setAttempt(value => value + 1),
  };
}
